import {
  Button,
  ButtonGroup,
  DropdownMenu,
  DropdownMenuCheckIcon,
  LucaPromptChip,
  LucaPromptChipRow
} from '@components'
import { ReportRenderFormat, REPORT_RENDER_FORMATS_BY_TYPE, ReportType } from '@constants'
import {
  INTERACTIVE_REPORT_TYPE,
  type IDashboardFilter,
  type IDateRangeKey,
  type InteractiveReportKey,
  type ReportComparisonMode
} from '@query'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaAngleDoubleDown,
  FaAngleDoubleUp,
  FaCalendarAlt,
  FaCaretDown,
  FaChartBar,
  FaFileCsv,
  FaFilePdf,
  FaFileWord,
  FaSquare,
  FaCheckSquare
} from 'react-icons/fa'
import { ReportTagFilter } from './ReportTagFilter'
import { Toolbar, ToolbarButtonGroup, ToolbarDivider, ToolbarGroup } from './interactive.styled'
import { ToolbarSplitSelect } from './ToolbarSplitSelect'

export type ReportOption = {
  key: InteractiveReportKey
  label: string
}

type SelectMenuItem = {
  key: string
  label: string
  selected: boolean
  onSelect: () => void
}

const EXPORT_FORMAT_MENU_OPTIONS = [
  {
    format: ReportRenderFormat.PDF,
    key: 'pdf',
    Icon: FaFilePdf,
    labelKey: 'reports.interactive.exportPdf',
    condensedLabelKey: 'reports.interactive.exportPdfCondensed'
  },
  {
    format: ReportRenderFormat.DOCX,
    key: 'docx',
    Icon: FaFileWord,
    labelKey: 'reports.interactive.exportDocx'
  },
  {
    format: ReportRenderFormat.CSV,
    key: 'csv',
    Icon: FaFileCsv,
    labelKey: 'reports.interactive.exportCsv',
    condensedLabelKey: 'reports.interactive.exportCsvCondensed'
  }
] as const

/** Legacy Reports “tiivistetty” download: fewer heading rows, no account lines. */
const CONDENSED_EXPORT_TYPE = {
  [ReportType.BALANCE_SHEET]: ReportType.BALANCE_SHEET_SHORT,
  [ReportType.INCOME_STATEMENT]: ReportType.INCOME_STATEMENT_SHORT
} as const

const menuButtonGroup = (key: string, children: React.ReactNode): React.ReactElement[] => [
  <ButtonGroup key={key}>{children}</ButtonGroup>
]

const selectMenuItems = (groupKey: string, items: SelectMenuItem[]): React.ReactElement[] =>
  menuButtonGroup(
    groupKey,
    items.map(item => (
      <Button
        key={item.key}
        type="button"
        icon={<DropdownMenuCheckIcon selected={item.selected} />}
        onClick={item.onSelect}
      >
        {item.label}
      </Button>
    ))
  )

type Props = {
  reportOptions: ReportOption[]
  reportKey: InteractiveReportKey
  onReportChange: (key: InteractiveReportKey) => void
  hasTags: boolean
  tagIds: number[]
  onTagIdsChange: (ids: number[]) => void
  /** Single-column reports keep a toolbar date chip; multi-column ones edit periods in the header. */
  showPeriodChip: boolean
  periodChipLabel: string
  /** Dashboard filter options; `custom` opens the date dialog. */
  periodFilters: IDashboardFilter[]
  selectedPeriodKey?: IDateRangeKey
  onPeriodFilterSelect: (key: IDateRangeKey) => void
  showComparison: boolean
  onShowComparisonChange: (value: boolean) => void
  comparisonMode: ReportComparisonMode
  onComparisonModeChange: (value: ReportComparisonMode) => void
  comparisonAvailable: boolean
  comparisonDisabled: boolean
  comparisonDisabledHint: string
  onExpandAll: () => void
  onCollapseAll: () => void
  onExport: (format: ReportRenderFormat, type: ReportType) => void
  isExporting: boolean
  lucaHints: string[]
  onLucaHint: (prompt: string) => void
}

export const ReportToolbar: React.FC<Props> = ({
  reportOptions,
  reportKey,
  onReportChange,
  hasTags,
  tagIds,
  onTagIdsChange,
  showPeriodChip,
  periodChipLabel,
  periodFilters,
  selectedPeriodKey,
  onPeriodFilterSelect,
  showComparison,
  onShowComparisonChange,
  comparisonMode,
  onComparisonModeChange,
  comparisonAvailable,
  comparisonDisabled,
  comparisonDisabledHint,
  onExpandAll,
  onCollapseAll,
  onExport,
  isExporting,
  lucaHints,
  onLucaHint
}) => {
  const [t] = useTranslation()
  const currentLabel =
    reportOptions.find(option => option.key === reportKey)?.label ||
    t('reports.interactive.reportType')

  const reportType: ReportType = INTERACTIVE_REPORT_TYPE[reportKey]
  const exportFormats = REPORT_RENDER_FORMATS_BY_TYPE[reportType]
  const condensedType =
    reportType === ReportType.BALANCE_SHEET || reportType === ReportType.INCOME_STATEMENT
      ? CONDENSED_EXPORT_TYPE[reportType]
      : undefined

  const exportMenuItems = useMemo(() => {
    const items = EXPORT_FORMAT_MENU_OPTIONS.filter(({ format }) =>
      exportFormats.includes(format)
    ).flatMap(option => {
      const main = {
        key: option.key,
        format: option.format,
        Icon: option.Icon,
        labelKey: option.labelKey,
        type: reportType
      }
      if (!condensedType || !('condensedLabelKey' in option)) {
        return [main]
      }
      return [
        main,
        {
          key: `${option.key}-condensed`,
          format: option.format,
          Icon: option.Icon,
          labelKey: option.condensedLabelKey,
          type: condensedType
        }
      ]
    })

    return menuButtonGroup(
      'formats',
      items.map(item => {
        const Icon = item.Icon
        return (
          <Button
            key={item.key}
            type="button"
            icon={<Icon />}
            disabled={isExporting}
            onClick={() => onExport(item.format, item.type)}
          >
            {t(item.labelKey)}
          </Button>
        )
      })
    )
  }, [condensedType, exportFormats, isExporting, onExport, reportType, t])

  const reportMenuItems = useMemo(
    () =>
      selectMenuItems(
        'reports',
        reportOptions.map(option => ({
          key: option.key,
          label: option.label,
          selected: reportKey === option.key,
          onSelect: () => onReportChange(option.key)
        }))
      ),
    [onReportChange, reportKey, reportOptions]
  )

  const periodMenuItems = useMemo(
    () =>
      selectMenuItems(
        'periods',
        periodFilters.map(filter => ({
          key: filter.key,
          label: t(`components.dateRangeFilter.${filter.key}`),
          selected: selectedPeriodKey === filter.key,
          onSelect: () => onPeriodFilterSelect(filter.key)
        }))
      ),
    [onPeriodFilterSelect, periodFilters, selectedPeriodKey, t]
  )

  return (
    <>
      <Toolbar>
        <ToolbarGroup>
          <ToolbarSplitSelect
            icon={<FaChartBar style={{ color: '#1b98f5' }} />}
            label={currentLabel}
            menuItems={reportMenuItems}
          />

          {showPeriodChip && (
            <ToolbarSplitSelect
              icon={<FaCalendarAlt style={{ color: '#a0aab5' }} />}
              label={periodChipLabel}
              title={periodChipLabel}
              menuItems={periodMenuItems}
            />
          )}

          {hasTags && <ReportTagFilter tagIds={tagIds} onTagIdsChange={onTagIdsChange} />}

          {comparisonAvailable && (
            <Button
              type="button"
              size="sm"
              icon={showComparison ? <FaCheckSquare style={{ color: '#1b98f5' }} /> : <FaSquare />}
              disabled={comparisonDisabled}
              title={comparisonDisabled ? comparisonDisabledHint : undefined}
              onClick={() => onShowComparisonChange(!showComparison)}
            >
              {t('reports.interactive.showComparison')}
            </Button>
          )}

          {comparisonAvailable && showComparison && !comparisonDisabled && (
            <ToolbarButtonGroup>
              <Button
                type="button"
                size="sm"
                style={comparisonMode === 'amount' ? { color: '#1b98f5' } : undefined}
                onClick={() => onComparisonModeChange('amount')}
              >
                {t('reports.interactive.comparisonAmount')}
              </Button>
              <Button
                type="button"
                size="sm"
                style={comparisonMode === 'percent' ? { color: '#1b98f5' } : undefined}
                onClick={() => onComparisonModeChange('percent')}
              >
                {t('reports.interactive.comparisonPercent')}
              </Button>
            </ToolbarButtonGroup>
          )}
        </ToolbarGroup>

        <ToolbarGroup $nowrap>
          <ToolbarDivider />
          <ToolbarButtonGroup>
            <Button
              type="button"
              size="sm"
              icon={<FaAngleDoubleDown />}
              title={t('reports.interactive.expandAll')}
              onClick={onExpandAll}
            />
            <Button
              type="button"
              size="sm"
              icon={<FaAngleDoubleUp />}
              title={t('reports.interactive.collapseAll')}
              onClick={onCollapseAll}
            />
          </ToolbarButtonGroup>
        </ToolbarGroup>

        <ToolbarGroup $end>
          <DropdownMenu items={exportMenuItems}>
            <Button
              type="button"
              size="sm"
              iconRight={<FaCaretDown />}
              showSpinner={isExporting}
              disabled={isExporting}
            >
              {t('reports.interactive.export')}
            </Button>
          </DropdownMenu>
        </ToolbarGroup>
      </Toolbar>

      {lucaHints.length > 0 && (
        <LucaPromptChipRow>
          {lucaHints.map(hint => (
            <LucaPromptChip key={hint} label={hint} onClick={() => onLucaHint(hint)} />
          ))}
        </LucaPromptChipRow>
      )}
    </>
  )
}
